import type { TranslationKey } from '../i18n/keys';
import type {
  FantasyAnswerValue,
  FantasyMatchEntry,
} from '../types/fantasyMatch';

/** The four answers to a fantasy, in the order the cards are shown. */
export const FANTASY_ANSWERS: readonly FantasyAnswerValue[] = [
  'love',
  'maybe',
  'fantasy',
  'no',
];

/**
 * The three answers of the same-device Fantasy Explorer, which still rates on
 * its own 1-5 scale (no "only as a fantasy" there).
 */
export type RatingAnswer = Exclude<FantasyAnswerValue, 'fantasy'>;
export const RATING_ANSWERS: readonly RatingAnswer[] = ['love', 'maybe', 'no'];

export function isFantasyAnswer(value: unknown): value is FantasyAnswerValue {
  return (
    value === 'love' ||
    value === 'maybe' ||
    value === 'fantasy' ||
    value === 'no'
  );
}

/**
 * Illustration recipes of the four answer cards (`renderScene`, see
 * `src/art/doodle/scene.mjs`). Fixed: the same pictures everywhere.
 */
export const FANTASY_ANSWER_ART: Record<FantasyAnswerValue, unknown> = {
  love: {
    cast: [
      {
        who: 'her',
        x: 36,
        y: 96,
        size: 0.95,
        arms: 'hug',
        legs: 'tiptoe',
        face: 'love',
      },
      {
        who: 'him',
        x: 62,
        y: 96,
        size: 0.95,
        arms: 'hug',
        legs: 'stand',
        face: 'love',
      },
    ],
    props: [
      { name: 'heart', x: 50, y: 16, size: 1.2, tone: 'lipstick' },
      { name: 'flame', x: 84, y: 24, size: 0.9, tone: 'lipstick' },
      { name: 'heart', x: 18, y: 26, size: 0.6, tone: 'blush' },
    ],
    bubble: 'blush',
  },
  maybe: {
    cast: [
      {
        who: 'her',
        x: 32,
        y: 96,
        size: 0.9,
        arms: 'point',
        legs: 'stand',
        face: 'smirk',
      },
      {
        who: 'him',
        x: 64,
        y: 96,
        size: 0.9,
        arms: 'shy',
        legs: 'stand',
        face: 'blush',
      },
    ],
    props: [
      { name: 'thought', x: 82, y: 22, size: 1.1, tone: 'lavender' },
      { name: 'question', x: 82, y: 21, size: 0.55, tone: 'gold' },
    ],
    bubble: 'lavender',
  },
  fantasy: {
    cast: [
      {
        who: 'her',
        bust: true,
        x: 30,
        y: 62,
        size: 0.95,
        face: 'sleepy',
      },
      {
        who: 'him',
        bust: true,
        x: 52,
        y: 66,
        size: 0.85,
        face: 'sleepy',
      },
    ],
    props: [
      { name: 'thought', x: 76, y: 28, size: 1.15, tone: 'lavender' },
      { name: 'moon', x: 18, y: 18, size: 0.8, tone: 'gold' },
      { name: 'star', x: 90, y: 62, size: 0.55, tone: 'gold' },
    ],
    bubble: 'lavender',
  },
  no: {
    cast: [
      {
        who: 'her',
        x: 30,
        y: 96,
        size: 0.85,
        arms: 'hip',
        legs: 'stand',
        face: 'smirk',
      },
    ],
    props: [
      { name: 'shield', x: 68, y: 50, size: 1.5, tone: 'line' },
      { name: 'cross', x: 68, y: 48, size: 0.7, tone: 'lipstick' },
      { name: 'heartLine', x: 68, y: 82, size: 0.6, tone: 'blush' },
    ],
    bubble: 'gold',
  },
};

export const FANTASY_ANSWER_LABEL_KEY: Record<
  FantasyAnswerValue,
  TranslationKey
> = {
  love: 'fantasy.answer.love',
  maybe: 'fantasy.answer.maybe',
  fantasy: 'fantasy.answer.fantasy',
  no: 'fantasy.answer.no',
};

/** What the Fantasy Explorer game stores for each answer (its 1-5 scale). */
export const FANTASY_ANSWER_RATING: Record<RatingAnswer, number> = {
  love: 5,
  maybe: 3,
  no: 1,
};

/** 4-5 is love, 3 is maybe, anything lower is no. */
export function ratingToAnswer(rating: number): RatingAnswer {
  if (rating >= 4) return 'love';
  if (rating === 3) return 'maybe';
  return 'no';
}

/**
 * A match is a pair of love / maybe / fantasy. Both love is the strong kind;
 * a pair where at least one side said "only as a fantasy" is its own kind
 * (nothing to do, plenty to talk about); anything else is the curious kind.
 */
export function matchKind(
  a: FantasyAnswerValue,
  b: FantasyAnswerValue
): FantasyMatchEntry['kind'] | null {
  if (a === 'no' || b === 'no') return null;
  if (a === 'fantasy' || b === 'fantasy') return 'fantasy';
  return a === 'love' && b === 'love' ? 'both_love' : 'curious';
}

/**
 * The matches inside one Fantasy Explorer session: the picks both players
 * rated love or maybe. A `no`, and a pick only one of them rated, never shows.
 */
export function matchesFromRatings(
  first: Record<string, number> | null | undefined,
  second: Record<string, number> | null | undefined
): FantasyMatchEntry[] {
  if (!first || !second) return [];
  const matches: FantasyMatchEntry[] = [];
  for (const [slug, rating] of Object.entries(first)) {
    const other = second[slug];
    if (typeof rating !== 'number' || typeof other !== 'number') continue;
    const mine = ratingToAnswer(rating);
    const partner = ratingToAnswer(other);
    const kind = matchKind(mine, partner);
    if (kind) matches.push({ slug, mine, partner, kind });
  }
  return matches;
}
